import { useState, useMemo, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Swords, Rocket, Shield, Plane, Factory, Lock, TrendingUp, TrendingDown, Minus, Globe, Newspaper, ExternalLink, GitCompare, History } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import ArmsWorldMap from "./ArmsWorldMap";
import ArmsComparison from "./ArmsComparison";
import ArmsHistoryChart from "./ArmsHistoryChart";

// --- Curated OSINT Data (SIPRI, IISS Military Balance 2024, CSIS Missile Threat) ---

interface CountryArms {
  country: string;
  flag: string;
  icbm: number;
  srbm_mrbm: number;
  combatDrones: number;
  loiteringMunitions: number;
  samSystems: number;
  interceptors: number;
  fighters: number;
  cruiseMissiles: number;
  productionCapacity: number;
  readiness: number;
  trend: "building" | "stable" | "depleting";
  intelNotes: string[];
}

const ARMS_DATA: CountryArms[] = [
  {
    country: "Russia", flag: "🇷🇺",
    icbm: 527, srbm_mrbm: 350, combatDrones: 2800, loiteringMunitions: 5000,
    samSystems: 132, interceptors: 1800, fighters: 1511, cruiseMissiles: 900,
    productionCapacity: 72, readiness: 58, trend: "depleting",
    intelNotes: [
      "Shahed-136 licensed production at Alabuga ramp-up to ~300/month",
      "Iskander missile stocks depleted ~60% since Feb 2022; production struggling to replace losses",
      "S-400 batteries redeployed from Far East to Ukraine front",
    ],
  },
  {
    country: "United States", flag: "🇺🇸",
    icbm: 400, srbm_mrbm: 120, combatDrones: 950, loiteringMunitions: 2200,
    samSystems: 60, interceptors: 2400, fighters: 2740, cruiseMissiles: 4000,
    productionCapacity: 95, readiness: 92, trend: "building",
    intelNotes: [
      "Sentinel ICBM replacement program on track for 2029 IOC",
      "Replicator initiative scaling autonomous drone production to 1000+/year",
      "THAAD and Patriot interceptor production doubled under supplemental funding",
    ],
  },
  {
    country: "China", flag: "🇨🇳",
    icbm: 350, srbm_mrbm: 1200, combatDrones: 3200, loiteringMunitions: 4000,
    samSystems: 96, interceptors: 2800, fighters: 1900, cruiseMissiles: 3200,
    productionCapacity: 93, readiness: 88, trend: "building",
    intelNotes: [
      "DF-41 silo construction accelerated — est. 300+ silos by 2030",
      "Wing Loong III combat drone entering mass production for export",
      "HQ-9B SAM deployed in record numbers around Taiwan Strait",
    ],
  },
  {
    country: "Ukraine", flag: "🇺🇦",
    icbm: 0, srbm_mrbm: 30, combatDrones: 8000, loiteringMunitions: 25000,
    samSystems: 22, interceptors: 320, fighters: 98, cruiseMissiles: 50,
    productionCapacity: 65, readiness: 52, trend: "depleting",
    intelNotes: [
      "FPV drone production exceeds 100K/month domestically",
      "Air defense interceptor stocks critically low — dependent on Western resupply",
      "Neptune cruise missile production limited but ongoing",
    ],
  },
  {
    country: "Iran", flag: "🇮🇷",
    icbm: 0, srbm_mrbm: 3000, combatDrones: 3500, loiteringMunitions: 6000,
    samSystems: 40, interceptors: 600, fighters: 186, cruiseMissiles: 500,
    productionCapacity: 78, readiness: 74, trend: "building",
    intelNotes: [
      "Shahed-136 mass production hub — primary export to Russia",
      "Fattah-2 hypersonic missile entered production phase",
      "Bavar-373 SAM operational with improved engagement range",
    ],
  },
  {
    country: "Israel", flag: "🇮🇱",
    icbm: 0, srbm_mrbm: 150, combatDrones: 600, loiteringMunitions: 3000,
    samSystems: 18, interceptors: 2000, fighters: 339, cruiseMissiles: 200,
    productionCapacity: 88, readiness: 82, trend: "depleting",
    intelNotes: [
      "Iron Dome interceptor stocks drawn down heavily post-Oct 7",
      "Arrow-3 exo-atmospheric interceptor proving effective against ballistic threats",
      "Hermes 900/450 drone fleet expanded for persistent ISR",
    ],
  },
  {
    country: "North Korea", flag: "🇰🇵",
    icbm: 50, srbm_mrbm: 600, combatDrones: 400, loiteringMunitions: 800,
    samSystems: 45, interceptors: 500, fighters: 458, cruiseMissiles: 100,
    productionCapacity: 55, readiness: 60, trend: "stable",
    intelNotes: [
      "Hwasong-18 solid-fuel ICBM now in serial production",
      "KN-23/24 SRBMs exported to Russia for Ukraine operations",
      "Air defense largely obsolete Soviet-era systems",
    ],
  },
  {
    country: "India", flag: "🇮🇳",
    icbm: 12, srbm_mrbm: 250, combatDrones: 200, loiteringMunitions: 500,
    samSystems: 32, interceptors: 900, fighters: 600, cruiseMissiles: 800,
    productionCapacity: 60, readiness: 72, trend: "building",
    intelNotes: [
      "Agni-V MIRV test successful — nuclear triad maturing",
      "BrahMos cruise missile production ramping for domestic + export",
      "S-400 batteries fully operational along Pakistan/China borders",
    ],
  },
  {
    country: "Turkey", flag: "🇹🇷",
    icbm: 0, srbm_mrbm: 50, combatDrones: 800, loiteringMunitions: 2500,
    samSystems: 10, interceptors: 300, fighters: 240, cruiseMissiles: 150,
    productionCapacity: 70, readiness: 68, trend: "building",
    intelNotes: [
      "Bayraktar TB3 carrier-capable drone entering production",
      "KAAN 5th-gen fighter first flight completed — IOC expected 2028",
      "HISAR-O+ medium-range SAM in serial production",
    ],
  },
  {
    country: "France", flag: "🇫🇷",
    icbm: 0, srbm_mrbm: 0, combatDrones: 30, loiteringMunitions: 100,
    samSystems: 12, interceptors: 400, fighters: 234, cruiseMissiles: 500,
    productionCapacity: 75, readiness: 78, trend: "stable",
    intelNotes: [
      "ASMP-A nuclear cruise missile modernization complete",
      "SAMP/T air defense deployed to Eastern Europe",
      "Rafale F4 standard entering service with enhanced EW suite",
    ],
  },
  {
    country: "UK", flag: "🇬🇧",
    icbm: 0, srbm_mrbm: 0, combatDrones: 20, loiteringMunitions: 60,
    samSystems: 8, interceptors: 250, fighters: 137, cruiseMissiles: 250,
    productionCapacity: 55, readiness: 70, trend: "stable",
    intelNotes: [
      "Storm Shadow stocks depleted by Ukraine donations",
      "AUKUS defense industrial base investments increasing capacity",
      "Sky Sabre SAM deployed to Poland for NATO eastern flank",
    ],
  },
  {
    country: "South Korea", flag: "🇰🇷",
    icbm: 0, srbm_mrbm: 200, combatDrones: 150, loiteringMunitions: 400,
    samSystems: 20, interceptors: 700, fighters: 410, cruiseMissiles: 300,
    productionCapacity: 82, readiness: 80, trend: "building",
    intelNotes: [
      "Hyunmoo-5 'monster missile' for bunker busting in production",
      "KF-21 Boramae fighter entering low-rate production",
      "L-SAM ballistic missile defense system in testing",
    ],
  },
  {
    country: "Pakistan", flag: "🇵🇰",
    icbm: 0, srbm_mrbm: 200, combatDrones: 60, loiteringMunitions: 200,
    samSystems: 15, interceptors: 350, fighters: 370, cruiseMissiles: 100,
    productionCapacity: 45, readiness: 58, trend: "stable",
    intelNotes: [
      "Shaheen-III MRBM provides 2750km range covering deep Indian targets",
      "JF-17 Block III production with PL-15 BVR missile integration",
      "HQ-9/P SAM procured from China for strategic site defense",
    ],
  },
  {
    country: "Japan", flag: "🇯🇵",
    icbm: 0, srbm_mrbm: 0, combatDrones: 20, loiteringMunitions: 50,
    samSystems: 24, interceptors: 500, fighters: 290, cruiseMissiles: 500,
    productionCapacity: 70, readiness: 75, trend: "building",
    intelNotes: [
      "Tomahawk cruise missile procurement for counterstrike capability",
      "PAC-3 MSE and Aegis Ashore expanding BMD umbrella",
      "F-X 6th-gen fighter development with UK and Italy (GCAP)",
    ],
  },
  {
    country: "Saudi Arabia", flag: "🇸🇦",
    icbm: 0, srbm_mrbm: 120, combatDrones: 300, loiteringMunitions: 600,
    samSystems: 16, interceptors: 800, fighters: 270, cruiseMissiles: 200,
    productionCapacity: 20, readiness: 65, trend: "building",
    intelNotes: [
      "Patriot PAC-3 interceptor stocks heavily consumed vs Houthi attacks",
      "Chinese DF-21 MRBMs in inventory — undisclosed warhead type",
      "Wing Loong II drone fleet expanded for Yemen operations",
    ],
  },
];

const FREE_COUNTRY_LIMIT = 5;

const getStatusLabel = (score: number) => {
  if (score > 80) return { label: "FULL CAPACITY", color: "text-green-400", bg: "bg-green-400/10" };
  if (score > 50) return { label: "OPERATIONAL", color: "text-yellow-400", bg: "bg-yellow-400/10" };
  if (score > 25) return { label: "DEPLETED", color: "text-red-400", bg: "bg-red-400/10" };
  return { label: "CRITICAL SHORTAGE", color: "text-red-500 animate-pulse", bg: "bg-red-500/10" };
};

const getTrendIcon = (trend: string) => {
  if (trend === "building") return <TrendingUp className="h-3.5 w-3.5 text-green-400" />;
  if (trend === "depleting") return <TrendingDown className="h-3.5 w-3.5 text-red-400" />;
  return <Minus className="h-3.5 w-3.5 text-yellow-400" />;
};

const CATEGORY_COLORS: Record<string, string> = {
  transfer: "bg-blue-400/10 text-blue-400",
  production: "bg-green-400/10 text-green-400",
  deployment: "bg-purple-400/10 text-purple-400",
  depletion: "bg-red-400/10 text-red-400",
  general: "bg-muted text-muted-foreground",
};

const CONFLICT_ZONES = ["Ukraine", "Israel", "Iran", "North Korea", "Saudi Arabia"];

const ArmsInventoryMonitor = () => {
  const { isPremium } = useAuth();
  const [selectedCountry, setSelectedCountry] = useState("Russia");
  const [showComparison, setShowComparison] = useState(false);
  const [compareA, setCompareA] = useState("Russia");
  const [compareB, setCompareB] = useState("United States");
  const { permission, sendNotification } = usePushNotifications();
  const notifiedTransfersRef = useRef<Set<string>>(new Set());

  // Fetch live arms news
  const { data: liveNews } = useQuery({
    queryKey: ["arms-news"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-arms-news");
      if (error) throw error;
      return data as { news: Record<string, Array<{ headline: string; source: string; url: string; publishedAt: string; category: string }>>; fetchedAt: string };
    },
    refetchInterval: 10 * 60 * 1000, // 10 min
    staleTime: 5 * 60 * 1000,
  });

  const visibleData = useMemo(() => {
    return isPremium ? ARMS_DATA : ARMS_DATA.slice(0, FREE_COUNTRY_LIMIT);
  }, [isPremium]);

  const selected = useMemo(() => ARMS_DATA.find((d) => d.country === selectedCountry) ?? ARMS_DATA[0], [selectedCountry]);

  // Map data
  const mapData = useMemo(
    () =>
      visibleData.map((d) => ({
        country: d.country,
        flag: d.flag,
        readiness: d.readiness,
        trend: d.trend,
        totalInventory: d.icbm + d.srbm_mrbm + d.combatDrones + d.loiteringMunitions + d.interceptors + d.fighters + d.cruiseMissiles,
      })),
    [visibleData],
  );

  // Bar chart data
  const barData = useMemo(
    () =>
      visibleData.map((d) => ({
        name: `${d.flag} ${d.country}`,
        "Ballistic Missiles": d.icbm + d.srbm_mrbm,
        "Combat Drones": d.combatDrones,
        "Air Defense": d.samSystems * 20 + d.interceptors,
        "Air Attack": d.fighters + d.cruiseMissiles,
      })),
    [visibleData],
  );

  // Radar chart data
  const radarData = useMemo(
    () => [
      { axis: "Ballistic", value: Math.min(100, ((selected.icbm + selected.srbm_mrbm) / 3500) * 100) },
      { axis: "Drones", value: Math.min(100, ((selected.combatDrones + selected.loiteringMunitions) / 30000) * 100) },
      { axis: "Air Defense", value: Math.min(100, (selected.interceptors / 2800) * 100) },
      { axis: "Air Attack", value: Math.min(100, ((selected.fighters + selected.cruiseMissiles) / 6700) * 100) },
      { axis: "Production", value: selected.productionCapacity },
    ],
    [selected],
  );

  const status = getStatusLabel(selected.readiness);
  const countryNews = liveNews?.news?.[selected.country] ?? [];

  // Merge static intel + live news for display
  const allIntel = useMemo(() => {
    const staticNotes = selected.intelNotes.map((n) => ({ text: n, source: "OSINT", url: "", category: "general" }));
    const liveNotes = countryNews.map((n) => ({ text: n.headline, source: n.source, url: n.url, category: n.category }));
    return [...liveNotes, ...staticNotes];
  }, [selected, countryNews]);

  // Push notifications for arms transfers to conflict zones
  useEffect(() => {
    if (permission !== "granted" || !liveNews?.news) return;
    for (const country of CONFLICT_ZONES) {
      const news = liveNews.news[country];
      if (!news) continue;
      const transfers = news.filter((n) => n.category === "transfer");
      for (const t of transfers) {
        const key = t.headline.slice(0, 50);
        if (notifiedTransfersRef.current.has(key)) continue;
        notifiedTransfersRef.current.add(key);
        sendNotification(`🚨 ARMS TRANSFER: ${country}`, {
          body: t.headline,
          tag: `arms-transfer-${key}`,
          requireInteraction: true,
        });
      }
    }
  }, [liveNews, permission, sendNotification]);

  return (
    <section className="container mx-auto px-4 py-8 space-y-6">
      <Card className="border-primary/20 bg-card/80 backdrop-blur">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Swords className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Arms &amp; Defense Inventory Monitor</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs border-primary/30">OSINT • SIPRI / IISS</Badge>
              <Badge variant="outline" className="text-xs border-yellow-500/40 text-yellow-400">ESTIMATES</Badge>
              {liveNews && (
                <Badge variant="outline" className="text-xs border-green-500/40 text-green-400">
                  <Newspaper className="h-3 w-3 mr-1" /> LIVE NEWS
                </Badge>
              )}
            </div>
          </div>
          <CardDescription className="text-xs">
            Real-time estimates of ballistic missiles, drones, air defense &amp; air attack inventories across 15 military powers.
            Click a country on the map to view its detailed profile.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* World Map */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
              <Globe className="h-4 w-4" /> Global Arms Density Map
            </h4>
            <ArmsWorldMap data={mapData} selectedCountry={selectedCountry} onSelectCountry={setSelectedCountry} />
            <p className="text-[10px] text-muted-foreground mt-1.5">
              Circle size = total inventory volume · Color = readiness (green = full, red = critical)
            </p>
          </div>

          {/* Country Comparison Mode */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-1.5">
                <GitCompare className="h-4 w-4" /> Side-by-Side Comparison
              </h4>
              <Button
                variant={showComparison ? "default" : "outline"}
                size="sm"
                className="text-xs h-7"
                onClick={() => setShowComparison(!showComparison)}
              >
                {showComparison ? "Hide" : "Compare Countries"}
              </Button>
            </div>
            {showComparison && (
              <ArmsComparison
                allCountries={ARMS_DATA}
                countryA={compareA}
                countryB={compareB}
                onChangeA={setCompareA}
                onChangeB={setCompareB}
              />
            )}
          </div>

          {/* Stacked Bar Chart */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
              <Rocket className="h-4 w-4" /> Inventory Comparison
            </h4>
            <div className="h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} layout="vertical" margin={{ left: 10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                  <XAxis type="number" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" width={110} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11 }}
                    labelStyle={{ color: "hsl(var(--foreground))" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="Ballistic Missiles" stackId="a" fill="hsl(0, 80%, 55%)" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="Combat Drones" stackId="a" fill="hsl(35, 90%, 55%)" />
                  <Bar dataKey="Air Defense" stackId="a" fill="hsl(200, 80%, 55%)" />
                  <Bar dataKey="Air Attack" stackId="a" fill="hsl(260, 70%, 60%)" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Historical Timeline */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
              <History className="h-4 w-4" /> Historical Inventory Evolution — {selected.flag} {selected.country}
            </h4>
            <ArmsHistoryChart selectedCountry={selected.country} />
          </div>

          {/* Country Selector + Radar */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
                <Shield className="h-4 w-4" /> Capability Profile
              </h4>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="w-full mb-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ARMS_DATA.map((d) => (
                    <SelectItem key={d.country} value={d.country}>
                      {d.flag} {d.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} outerRadius="75%">
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="axis" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar name={selected.country} dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.25} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Country Detail Card */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold">{selected.flag} {selected.country}</span>
                <Badge className={`${status.bg} ${status.color} border-0 text-xs`}>{status.label}</Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                {[
                  { label: "ICBMs", value: selected.icbm },
                  { label: "SRBMs/MRBMs", value: selected.srbm_mrbm },
                  { label: "Combat Drones", value: selected.combatDrones },
                  { label: "Loitering Munitions", value: selected.loiteringMunitions },
                  { label: "SAM Systems", value: selected.samSystems },
                  { label: "Interceptors", value: selected.interceptors },
                  { label: "Fighters", value: selected.fighters },
                  { label: "Cruise Missiles", value: selected.cruiseMissiles },
                ].map((item) => (
                  <div key={item.label} className="bg-muted/30 rounded-md p-2.5">
                    <span className="text-muted-foreground">{item.label}</span>
                    <p className="font-bold text-foreground">{item.value.toLocaleString()}</p>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">Trend:</span>
                {getTrendIcon(selected.trend)}
                <span className="capitalize text-foreground">{selected.trend}</span>
                <span className="text-muted-foreground ml-auto">Production: {selected.productionCapacity}%</span>
              </div>

              {/* Intel notes — merged static + live */}
              <div className="space-y-1.5 mt-2">
                <h5 className="text-xs font-semibold text-primary flex items-center gap-1">
                  <Plane className="h-3 w-3" /> Intel Notes
                  {countryNews.length > 0 && (
                    <Badge className="ml-1 text-[9px] bg-green-400/10 text-green-400 border-0">{countryNews.length} LIVE</Badge>
                  )}
                </h5>
                {(isPremium ? allIntel : allIntel.slice(0, 2)).map((note, i) => (
                  <div key={i} className={`text-[11px] leading-snug pl-3 border-l-2 ${note.source !== "OSINT" ? "border-green-400/40" : "border-primary/20"}`}>
                    <span className="text-muted-foreground">{note.text}</span>
                    {note.source !== "OSINT" && (
                      <span className="ml-1.5 inline-flex items-center gap-0.5">
                        <Badge className={`text-[8px] border-0 py-0 px-1 ${CATEGORY_COLORS[note.category] ?? CATEGORY_COLORS.general}`}>
                          {note.category}
                        </Badge>
                        {note.url && (
                          <a href={note.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                            <ExternalLink className="h-2.5 w-2.5 inline" />
                          </a>
                        )}
                      </span>
                    )}
                  </div>
                ))}
                {!isPremium && allIntel.length > 2 && (
                  <p className="text-[10px] text-primary/60 flex items-center gap-1">
                    <Lock className="h-3 w-3" /> {allIntel.length - 2} more intel notes — Premium only
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Heat-style Table */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
              <Factory className="h-4 w-4" /> Full Inventory Table
            </h4>
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs">Country</TableHead>
                    <TableHead className="text-xs text-center">Ballistic</TableHead>
                    <TableHead className="text-xs text-center">Drones</TableHead>
                    <TableHead className="text-xs text-center">Air Def</TableHead>
                    <TableHead className="text-xs text-center">Fighters</TableHead>
                    <TableHead className="text-xs text-center">Readiness</TableHead>
                    <TableHead className="text-xs text-center">Trend</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleData.map((d) => {
                    const s = getStatusLabel(d.readiness);
                    const hasLiveNews = (liveNews?.news?.[d.country]?.length ?? 0) > 0;
                    return (
                      <TableRow
                        key={d.country}
                        className={`cursor-pointer hover:bg-muted/20 ${selectedCountry === d.country ? "bg-primary/5" : ""}`}
                        onClick={() => setSelectedCountry(d.country)}
                      >
                        <TableCell className="text-xs font-medium whitespace-nowrap">
                          {d.flag} {d.country}
                          {hasLiveNews && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-green-400" />}
                        </TableCell>
                        <TableCell className="text-xs text-center">{(d.icbm + d.srbm_mrbm).toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-center">{(d.combatDrones + d.loiteringMunitions).toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-center">{d.interceptors.toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-center">{d.fighters.toLocaleString()}</TableCell>
                        <TableCell className="text-xs text-center">
                          <Badge className={`${s.bg} ${s.color} border-0 text-[10px]`}>{s.label}</Badge>
                        </TableCell>
                        <TableCell className="text-center">{getTrendIcon(d.trend)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {!isPremium && (
              <div className="mt-3 text-center">
                <p className="text-xs text-muted-foreground mb-2">
                  <Lock className="inline h-3 w-3 mr-1" />
                  Showing {FREE_COUNTRY_LIMIT} of {ARMS_DATA.length} countries — Upgrade to Premium for full access + detailed intel notes
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default ArmsInventoryMonitor;
